import {
    Chip,
    Grid,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { iconSize } from 'assets/theme/iconSize';
import { ITableProps } from '@types_main/itable.props';
import { ProfileResponse } from '../services/responses/profile.response';
import { ProfileService } from '../services/profile.service';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { useTranslation } from 'react-i18next';

export function ProfileTable(props: ITableProps<ProfileResponse>) {
    const { t } = useTranslation();
    const { data, editAction } = props;
    const [, dispatch] = useMaterialUIController();
    const { showAlert, alertDelete } = useAlert();

    const remove = (id: string) => {
        setLoading(dispatch, true);

        new ProfileService()
            .deleteById(id)
            .then(() => {
                showAlert(t('perfis.excluirSucesso'), 'success', 2500);
                props.setRefresh(!props.refresh);
            })
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 3000))
            .finally(() => setLoading(dispatch, false));
    };

    return (
        <Grid>
            <TableContainer sx={{ marginTop: '15px', maxHeight: '60vh', overflowY: 'auto' }}>
                <Table size="medium">
                    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                        <TableRow hover>
                            <TableCell width="40%">{t('general.nome')}</TableCell>
                            <TableCell width="25%">{t('perfis.telasLiberadas')}</TableCell>
                            <TableCell width="20%">{t('perfis.situacao')}</TableCell>
                            <TableCell width="15%" align="center">
                                {t('general.acoes')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.data ? (
                            data.data.records.length > 0 ? (
                                data.data.records.map((item) => (
                                    <TableRow hover key={item.id}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>
                                            {/* Contagem em vez da lista: quinze nomes não cabem numa célula. */}
                                            {item.screens.length}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={item.active
                                                    ? t('perfis.ativo')
                                                    : t('perfis.inativo')}
                                                color={item.active ? 'success' : 'default'}
                                                variant={item.active ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <Tooltip title={t('general.editar')}>
                                                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => editAction(item.id)}>
                                                        <EditIcon
                                                            style={iconSize}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('general.excluir')}>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0.25 }}
                                                        onClick={() => alertDelete(
                                                            item.id,
                                                            remove,
                                                            item.name)}
                                                    >
                                                        <DeleteIcon
                                                            sx={iconSize}
                                                            color="error"
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow hover>
                                    <TableCell colSpan={4} align="center">
                                        {t('general.nenhumRegistro')}
                                    </TableCell>
                                </TableRow>
                            )
                        ) : (
                            Array.from({ length: 5 }, (_, index) => index).map((index) => (
                                <TableRow hover key={index}>
                                    {Array.from({ length: 4 }, (_, column) => column).map((column) => (
                                        <TableCell key={column}>
                                            <Skeleton height={30} variant="rectangular" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}
